import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { useEffect } from 'react';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set document title and meta tags
    document.title = 'Terms of Service | GrowthLink Digital Marketing';
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    updateMetaTag('description', 'Read GrowthLink\'s terms of service governing the use of our digital marketing services. Compliant with Indian law and Telangana jurisdiction.');
    updateMetaTag('robots', 'noindex, nofollow');
    
    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/terms-of-service`);
  }, []);

  const lastUpdated = new Date().toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppButton />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-darkGreen to-darkGreen/90">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 font-serif">
            Terms of Service
          </h1>
          <p className="text-xl text-white/90">
            Please read these terms carefully before using our services
          </p>
          <p className="text-sm text-white/70 mt-4">
            Last Updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Welcome to GrowthLink ("Company", "we", "our", "us"). These Terms of Service ("Terms", "Terms of Service") govern your use of our website located at growthlink.com (together or individually "Service") operated by GrowthLink.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users and others who access or use the Service.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>By accessing or using the Service you agree to be bound by these Terms. If you disagree with any part of the terms then you may not access the Service.</strong>
              </p>
            </div>

            {/* Services */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">2. Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                GrowthLink provides digital marketing services including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Search Engine Optimization (SEO)</li>
                <li>Social Media Marketing</li>
                <li>Content Strategy and Creation</li>
                <li>Pay-Per-Click (PPC) Advertising</li>
                <li>Brand Development and Strategy</li>
                <li>Digital Marketing Consultation</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The specific services provided to you will be outlined in a separate service agreement or proposal. We reserve the right to modify, suspend, or discontinue any aspect of our services at any time.
              </p>
            </div>

            {/* Account Registration */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">3. Account Registration</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you create an account with us, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password, whether your password is with our Service or a third-party service.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
              </p>
            </div>

            {/* Payment Terms */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">4. Payment Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>4.1 Fees and Charges:</strong> You agree to pay all fees or charges to your account based on the fees, charges, and billing terms in effect at the time a fee or charge is due and payable. All fees are quoted in Indian Rupees (INR) unless otherwise specified.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>4.2 Payment Methods:</strong> We accept payment through various methods including bank transfer, credit/debit cards, UPI, and other digital payment methods. Payment terms will be specified in your service agreement.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>4.3 Late Payments:</strong> Late payments may result in suspension of services. Interest may be charged on overdue amounts at the rate of 1.5% per month or the maximum rate permitted by law, whichever is lower.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>4.4 Refund Policy:</strong> Refunds are handled on a case-by-case basis. Generally, fees paid for services already rendered are non-refundable. Please refer to your service agreement for specific refund terms.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>4.5 Taxes:</strong> All fees are exclusive of applicable taxes (including GST). You are responsible for paying all taxes associated with your use of the Service.
              </p>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">5. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>5.1 Our Content:</strong> The Service and its original content (excluding Content provided by users), features and functionality are and will remain the exclusive property of GrowthLink and its licensors. The Service is protected by copyright, trademark, and other laws of both India and foreign countries.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>5.2 Your Content:</strong> You retain all rights to any content you submit, post or display on or through the Service. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display such content for the purpose of providing our services.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>5.3 Work Product:</strong> Unless otherwise specified in your service agreement, all work product created by GrowthLink in the course of providing services (including but not limited to marketing materials, strategies, and content) shall become your property upon full payment of all fees.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>5.4 Portfolio Rights:</strong> We reserve the right to use completed work in our portfolio and marketing materials unless you specifically request otherwise in writing.
              </p>
            </div>

            {/* User Responsibilities */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">6. User Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Use the Service for any illegal purpose or in violation of any local, state, national, or international law</li>
                <li>Violate or infringe upon the intellectual property rights of others</li>
                <li>Transmit any viruses, malware, or other malicious code</li>
                <li>Attempt to gain unauthorized access to any portion of the Service</li>
                <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
                <li>Use the Service to send spam or unsolicited communications</li>
                <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
                <li>Collect or store personal data about other users without their consent</li>
              </ul>
            </div>

            {/* Service Level and Warranties */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">7. Service Level and Warranties</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>7.1 Service Availability:</strong> While we strive to provide uninterrupted service, we do not guarantee that the Service will be available at all times. We may experience hardware, software, or other problems or need to perform maintenance related to the Service.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>7.2 No Guarantees:</strong> We do not guarantee specific results from our marketing services. Digital marketing results depend on many factors beyond our control, including market conditions, competition, and your business operations.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>7.3 Disclaimer:</strong> Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>7.4 Professional Advice:</strong> The information provided through our Service is for general informational purposes only and should not be considered as professional advice. You should consult with appropriate professionals for specific advice tailored to your situation.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">8. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                In no event shall GrowthLink, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Your access to or use of or inability to access or use the Service</li>
                <li>Any conduct or content of any third party on the Service</li>
                <li>Any content obtained from the Service</li>
                <li>Unauthorized access, use or alteration of your transmissions or content</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Our total liability to you for all claims arising from or related to the Service shall not exceed the amount you paid to us in the twelve (12) months preceding the claim.
              </p>
            </div>

            {/* Indemnification */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">9. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to defend, indemnify and hold harmless GrowthLink and its licensee and licensors, and their employees, contractors, agents, officers and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees), resulting from or arising out of: (a) your use and access of the Service, (b) your violation of any term of these Terms, (c) your violation of any third party right, including without limitation any copyright, property, or privacy right, or (d) any claim that your content caused damage to a third party.
              </p>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">10. Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>10.1 Termination by Us:</strong> We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will immediately cease.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>10.2 Termination by You:</strong> You may terminate your service agreement by providing written notice as specified in your service agreement. Early termination may result in fees as outlined in your service agreement.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>10.3 Effect of Termination:</strong> Upon termination, we will cease providing services. You remain responsible for all fees incurred prior to termination. We may retain your data as required by law or for legitimate business purposes.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>10.4 Survival:</strong> All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity and limitations of liability.
              </p>
            </div>

            {/* Confidentiality */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">11. Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Both parties agree to maintain the confidentiality of any proprietary or confidential information disclosed during the course of the business relationship. This includes but is not limited to business strategies, financial information, customer data, and trade secrets.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Confidential information does not include information that: (a) is or becomes publicly available through no breach of this agreement, (b) was rightfully in the receiving party's possession prior to disclosure, (c) is independently developed by the receiving party, or (d) is rightfully obtained from a third party without breach of any confidentiality obligation.
              </p>
            </div>

            {/* Dispute Resolution */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">12. Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>12.1 Negotiation:</strong> In the event of any dispute arising out of or relating to these Terms, the parties agree to first attempt to resolve the dispute through good faith negotiation.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>12.2 Mediation:</strong> If the dispute cannot be resolved through negotiation within thirty (30) days, the parties agree to attempt to resolve the dispute through mediation before a mutually agreed upon mediator.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>12.3 Arbitration:</strong> If mediation is unsuccessful, any unresolved dispute shall be settled by arbitration in accordance with the Arbitration and Conciliation Act, 1996. The arbitration shall be conducted in Hyderabad, Telangana, India.
              </p>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">13. Governing Law and Jurisdiction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms shall be governed and construed in accordance with the laws of India, without regard to its conflict of law provisions.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                The courts of Hyderabad, Telangana, India shall have exclusive jurisdiction over any disputes arising out of or relating to these Terms or the Service.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.
              </p>
            </div>

            {/* Changes to Terms */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">14. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.
              </p>
              <p className="text-gray-700 leading-relaxed">
                It is your responsibility to review these Terms periodically for changes. Your continued use of the Service following the posting of any changes constitutes acceptance of those changes.
              </p>
            </div>

            {/* Force Majeure */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">15. Force Majeure</h2>
              <p className="text-gray-700 leading-relaxed">
                GrowthLink shall not be liable for any failure or delay in performance due to circumstances beyond its reasonable control, including but not limited to acts of God, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, pandemics, strikes, or shortages of transportation facilities, fuel, energy, labor or materials.
              </p>
            </div>

            {/* Severability */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">16. Severability</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid under any applicable law, such unenforceability or invalidity shall not render these Terms unenforceable or invalid as a whole, and such provisions shall be deleted without affecting the remaining provisions herein.
              </p>
            </div>

            {/* Entire Agreement */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">17. Entire Agreement</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms, together with any service agreements, proposals, or other documents incorporated by reference, constitute the entire agreement between you and GrowthLink regarding the use of the Service, superseding any prior agreements between you and GrowthLink relating to your use of the Service.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-darkGreen mb-4 font-serif">18. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>GrowthLink</strong></p>
                <p className="text-gray-700 mb-2">Email: legal@growthlink.com</p>
                <p className="text-gray-700 mb-2">Phone: +91 (XXX) XXX-XXXX</p>
                <p className="text-gray-700">Address: Hyderabad, Telangana, India</p>
              </div>
            </div>

            {/* Acknowledgment */}
            <div className="bg-accent/10 p-8 rounded-lg border-l-4 border-accent">
              <h3 className="text-2xl font-bold text-darkGreen mb-4">Acknowledgment</h3>
              <p className="text-gray-700 leading-relaxed">
                By using our Service, you acknowledge that you have read these Terms of Service and agree to be bound by them. If you do not agree to these Terms, please do not use our Service.
              </p>
            </div>

          </div>

          {/* Back to Home */}
          <div className="mt-12 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-darkGreen text-white rounded-full hover:bg-darkGreen/90 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
